import { differenceInCalendarDays, format } from 'date-fns';
import { createDateSafe, formatDateSafe } from '@/utils/dateUtils';
import type {
  CollaboratorVacationSummary,
  VacationIneligibilityReason,
} from '@/features/Collaborators/Models/collaborator.types';

export type SummaryTone = 'default' | 'muted' | 'accruing' | 'success' | 'warning' | 'danger';

const parseIsoDate = (value: string | null | undefined): Date | null => createDateSafe(value);

function ineligibilityCaption(reason: VacationIneligibilityReason | null | undefined): {
  caption: string;
  tone: SummaryTone;
} {
  switch (reason) {
    case 'ContractType':
      return { caption: 'Contrato sem férias CLT', tone: 'muted' };
    case 'InactiveStatus':
      return { caption: 'Colaborador inativo', tone: 'muted' };
    case 'MissingAdmissionDate':
      return { caption: 'Sem data de admissão', tone: 'warning' };
    default:
      return { caption: '', tone: 'muted' };
  }
}

function isFirstYear(summary: CollaboratorVacationSummary, today: Date): boolean {
  if (!summary.eligible || !summary.entitledFrom) return false;
  const entitledFrom = parseIsoDate(summary.entitledFrom);
  return !!entitledFrom && entitledFrom > today;
}

export interface BalanceColumnValue {
  value: number | null;
  expiredDays: number;
  display: string;
  caption?: string;
  tone: SummaryTone;
  sortValue: number | undefined;
}

export function resolveSummaryBalance(
  summary: CollaboratorVacationSummary | undefined,
  today?: Date
): BalanceColumnValue {
  const currentDate = today || new Date();

  if (!summary) {
    return { value: null, expiredDays: 0, display: 'Não calculado', tone: 'muted', sortValue: undefined };
  }

  if (!summary.eligible) {
    const { caption, tone } = ineligibilityCaption(summary.ineligibilityReason);
    return { value: null, expiredDays: 0, display: '—', caption, tone, sortValue: undefined };
  }

  const balanceDays = summary.balanceDays ?? 0;
  const expiredDays = summary.expiredDays ?? 0;

  if (isFirstYear(summary, currentDate)) {
    return {
      value: 0,
      expiredDays,
      display: '0 dias',
      caption: `Em aquisição até ${formatDateSafe(summary.entitledFrom)}`,
      tone: 'accruing',
      sortValue: 0,
    };
  }

  return {
    value: balanceDays,
    expiredDays,
    display: `${balanceDays} dias`,
    caption: expiredDays > 0 ? `${expiredDays} vencidos` : undefined,
    tone: expiredDays > 0 ? 'danger' : 'default',
    sortValue: balanceDays,
  };
}

export interface ConcessiveColumnValue {
  display: string;
  caption?: string;
  tone: SummaryTone;
  startDate: string | null;
  endDate: string | null;
}

export function resolveSummaryConcessivePeriod(
  summary: CollaboratorVacationSummary | undefined,
  today?: Date
): ConcessiveColumnValue {
  const currentDate = today || new Date();

  if (!summary) {
    return { display: 'Não calculado', tone: 'muted', startDate: null, endDate: null };
  }

  if (!summary.eligible) {
    const { caption, tone } = ineligibilityCaption(summary.ineligibilityReason);
    return { display: 'Não se aplica', caption, tone, startDate: null, endDate: null };
  }

  if (!summary.concessivePeriod) {
    return { display: 'Não calculado', tone: 'muted', startDate: null, endDate: null };
  }

  const period = summary.concessivePeriod;
  const startFormatted = formatDateSafe(period.start);
  const endFormatted = formatDateSafe(period.end);

  const start = parseIsoDate(period.start);
  const end = parseIsoDate(period.end);

  if (start && start > currentDate) {
    if (isFirstYear(summary, currentDate)) {
      return {
        display: `Em aquisição até ${startFormatted}`,
        tone: 'accruing',
        startDate: period.start,
        endDate: period.end,
      };
    }
    return {
      display: endFormatted,
      caption: `Em aquisição até ${startFormatted}`,
      tone: 'accruing',
      startDate: period.start,
      endDate: period.end,
    };
  }

  if (end && end < currentDate) {
    return {
      display: endFormatted,
      caption: 'Vencido',
      tone: 'danger',
      startDate: period.start,
      endDate: period.end,
    };
  }

  return {
    display: endFormatted,
    caption: 'Vigente',
    tone: 'success',
    startDate: period.start,
    endDate: period.end,
  };
}

export interface EntitledFromColumnValue {
  display: string;
  caption?: string;
  tone: SummaryTone;
  date: string | null;
  sortValue: Date | null;
}

export function resolveSummaryEntitledFrom(
  summary: CollaboratorVacationSummary | undefined,
  today?: Date
): EntitledFromColumnValue {
  const currentDate = today || new Date();

  if (!summary) {
    return { display: 'Não calculado', tone: 'muted', date: null, sortValue: null };
  }

  if (!summary.eligible) {
    const { caption, tone } = ineligibilityCaption(summary.ineligibilityReason);
    return { display: 'Não se aplica', caption, tone, date: null, sortValue: null };
  }

  const start = summary.concessivePeriod?.start ?? null;
  const startDate = parseIsoDate(start);
  if (!start || !startDate) {
    return { display: 'Não calculado', tone: 'muted', date: null, sortValue: null };
  }

  const formatted = formatDateSafe(start);
  const isFuture = startDate > currentDate;

  if (!isFuture) {
    return {
      display: formatted,
      caption: 'com direito',
      tone: 'success',
      date: start,
      sortValue: startDate,
    };
  }

  const isFirstPeriod = summary.entitledFrom === start;
  return {
    display: formatted,
    caption: isFirstPeriod ? '1º período aquisitivo' : 'em aquisição',
    tone: 'accruing',
    date: start,
    sortValue: startDate,
  };
}

export interface SituationColumnValue {
  display: string;
  caption?: string;
  tone: SummaryTone;
  kind:
    | 'notCalculated'
    | 'notApplicable'
    | 'onVacation'
    | 'scheduled'
    | 'pendingApproval'
    | 'notEntitledYet'
    | 'expired'
    | 'noScheduling';
}

const PENDING_CAPTIONS: Record<string, string> = {
  PendingManager: 'do gestor',
  PendingClient: 'do cliente',
  PendingDP: 'do DP',
  PendingCorrection: 'correção pendente',
};

export function resolveSummarySituation(
  summary: CollaboratorVacationSummary | undefined,
  today?: Date
): SituationColumnValue {
  const currentDate = today || new Date();

  if (!summary) {
    return { display: 'Não calculado', tone: 'muted', kind: 'notCalculated' };
  }

  if (!summary.eligible) {
    const { caption, tone } = ineligibilityCaption(summary.ineligibilityReason);
    return { display: 'Não se aplica', caption, tone, kind: 'notApplicable' };
  }

  const todayStr = format(currentDate, 'yyyy-MM-dd');
  const nextVacation = summary.nextVacation;

  if (nextVacation) {
    const isOngoing = nextVacation.startDate <= todayStr && todayStr <= nextVacation.endDate;

    if (nextVacation.status === 'OnVacation' || (nextVacation.status === 'Scheduled' && isOngoing)) {
      const endFormatted = formatDateSafe(nextVacation.endDate);
      return {
        display: `Em férias até ${endFormatted}`,
        tone: 'success',
        kind: 'onVacation',
      };
    }

    if (nextVacation.status === 'Scheduled') {
      const startFormatted = formatDateSafe(nextVacation.startDate);
      const endFormatted = formatDateSafe(nextVacation.endDate);
      return {
        display: 'Agendada',
        caption: `${startFormatted} – ${endFormatted}`,
        tone: 'default',
        kind: 'scheduled',
      };
    }

    if (nextVacation.status?.startsWith('Pending')) {
      return {
        display: 'Aguardando aprovação',
        caption: PENDING_CAPTIONS[nextVacation.status],
        tone: 'warning',
        kind: 'pendingApproval',
      };
    }
  }

  if (isFirstYear(summary, currentDate)) {
    return {
      display: 'Ainda sem direito',
      tone: 'accruing',
      kind: 'notEntitledYet',
    };
  }

  if (summary.expiredDays && summary.expiredDays > 0) {
    return {
      display: 'Férias vencidas',
      tone: 'danger',
      kind: 'expired',
    };
  }

  return {
    display: 'Sem agendamento',
    tone: 'default',
    kind: 'noScheduling',
  };
}
